import axios from 'axios';

// IMPORTANT: Always use our own domain (coachshare.online) for the backend URL.
// DO NOT use Vercel-generated URLs as they change with each deployment.
// This ensures consistent routing and prevents 508 loop errors.
const api = axios.create({
  baseURL: 'https://api.coachshare.online',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['x-access-token'] = token;
    }

    // Log detailed request information
    console.log('🔍 API Request Details:', {
      fullUrl: `${config.baseURL}${config.url}`,
      method: config.method,
      headers: {
        ...config.headers,
        Authorization: config.headers.Authorization ? 'Bearer [REDACTED]' : undefined,
        'x-access-token': config.headers['x-access-token'] ? '[REDACTED]' : undefined
      },
      data: config.data,
      baseURL: config.baseURL,
      url: config.url,
      withCredentials: config.withCredentials,
      originalUrl: config.url,
      path: config.url.split('?')[0],
      query: config.url.includes('?') ? config.url.split('?')[1] : undefined,
      cookies: document.cookie,
      localStorage: {
        hasToken: !!localStorage.getItem('token'),
        allKeys: Object.keys(localStorage)
      },
      sessionStorage: {
        allKeys: Object.keys(sessionStorage)
      }
    });

    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', {
      message: error.message,
      stack: error.stack,
      code: error.code,
      name: error.name,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log detailed response information
    console.log('✅ API Response Details:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: response.data,
      config: {
        fullUrl: `${response.config.baseURL}${response.config.url}`,
        method: response.config.method,
        baseURL: response.config.baseURL,
        url: response.config.url,
        originalUrl: response.config.url,
        path: response.config.url.split('?')[0],
        query: response.config.url.includes('?') ? response.config.url.split('?')[1] : undefined,
        headers: {
          ...response.config.headers,
          Authorization: response.config.headers.Authorization ? 'Bearer [REDACTED]' : undefined,
          'x-access-token': response.config.headers['x-access-token'] ? '[REDACTED]' : undefined
        }
      },
      cookies: document.cookie,
      localStorage: {
        hasToken: !!localStorage.getItem('token'),
        allKeys: Object.keys(localStorage)
      },
      sessionStorage: {
        allKeys: Object.keys(sessionStorage)
      }
    });

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log('🔄 Attempting token refresh...');
        const response = await api.post('/auth/refresh-token', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.data?.token) {
          console.log('✅ Token refresh successful');
          localStorage.setItem('token', response.data.token);
          originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        // Clear token and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/auth/login';
        return Promise.reject(refreshError);
      }
    }

    // Log detailed error information
    console.error('❌ API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: error.config ? {
        fullUrl: `${error.config.baseURL}${error.config.url}`,
        method: error.config.method,
        baseURL: error.config.baseURL,
        url: error.config.url,
        originalUrl: error.config.url,
        path: error.config.url.split('?')[0],
        query: error.config.url.includes('?') ? error.config.url.split('?')[1] : undefined,
        headers: {
          ...error.config.headers,
          Authorization: error.config.headers.Authorization ? 'Bearer [REDACTED]' : undefined,
          'x-access-token': error.config.headers['x-access-token'] ? '[REDACTED]' : undefined
        }
      } : null,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      } : null,
      cookies: document.cookie,
      localStorage: {
        hasToken: !!localStorage.getItem('token'),
        allKeys: Object.keys(localStorage)
      },
      sessionStorage: {
        allKeys: Object.keys(sessionStorage)
      }
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
      console.log('🔒 Unauthorized access, clearing token...');
      localStorage.removeItem('token');
      // Don't redirect immediately, let the component handle it
      console.log('⚠️ 401 error - token cleared but not redirecting');
    }

    if (error.response?.status === 404) {
      console.error('🔍 404 Not Found:', {
        fullUrl: `${error.config?.baseURL}${error.config?.url}`,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        url: error.config?.url,
        originalUrl: error.config?.url,
        path: error.config?.url.split('?')[0],
        query: error.config?.url.includes('?') ? error.config?.url.split('?')[1] : undefined,
        headers: error.config?.headers,
        data: error.config?.data,
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers
        }
      });
    }

    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (credentials: { email: string; password: string }) => {
    try {
      console.log('🔑 Login attempt:', {
        email: credentials.email,
        baseURL: api.defaults.baseURL,
        url: '/api/auth/login',
        fullUrl: `${api.defaults.baseURL}/api/auth/login`,
        localStorage: {
          hasToken: !!localStorage.getItem('token'),
          tokenLength: localStorage.getItem('token')?.length,
          allKeys: Object.keys(localStorage)
        },
        sessionStorage: {
          allKeys: Object.keys(sessionStorage)
        },
        cookies: document.cookie
      });
      
      const response = await api.post('/api/auth/login', credentials);
      console.log('📥 Login response:', {
        hasToken: !!response.data.token,
        hasUserData: !!response.data.user,
        status: response.status,
        responseData: {
          ...response.data,
          token: response.data.token ? '[REDACTED]' : null,
          user: response.data.user ? {
            id: response.data.user._id,
            email: response.data.user.email,
            role: response.data.user.role,
            isVerified: response.data.user.isEmailVerified
          } : null
        },
        headers: {
          ...response.headers,
          'set-cookie': response.headers['set-cookie'] ? '[REDACTED]' : undefined
        }
      });

      if (response.data.token) {
        console.log('💾 Storing token:', {
          tokenLength: response.data.token.length,
          localStorageBefore: {
            hasToken: !!localStorage.getItem('token'),
            allKeys: Object.keys(localStorage)
          }
        });
        
        localStorage.setItem('token', response.data.token);
        
        console.log('💾 Token stored:', {
          localStorageAfter: {
            hasToken: !!localStorage.getItem('token'),
            tokenLength: localStorage.getItem('token')?.length,
            allKeys: Object.keys(localStorage)
          }
        });
        
        return response.data;
      }
      
      throw new Error('Login failed: No token in response');
    } catch (error) {
      console.error('❌ Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        localStorage: {
          hasToken: !!localStorage.getItem('token'),
          allKeys: Object.keys(localStorage)
        },
        sessionStorage: {
          allKeys: Object.keys(sessionStorage)
        },
        cookies: document.cookie
      });
      throw error;
    }
  },

  refreshToken: async () => {
    try {
      const response = await api.post('/api/auth/refresh-token');
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/api/auth/logout');
    localStorage.removeItem('token');
    return response.data;
  },

  resendVerification: async (email: string) => {
    try {
      const response = await api.post('/api/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  }
};

export default api;

const getToken = (): string | null => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ No token found in localStorage:', {
        localStorageKeys: Object.keys(localStorage),
        sessionStorageKeys: Object.keys(sessionStorage),
        cookies: document.cookie
      });
      return null;
    }
    return token;
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
}; 